<script setup lang="ts">
import { computed } from 'vue'
import { useVoiceStore } from '@/stores/voice'
import { Icon } from '#components'

const voiceStore = useVoiceStore()
const isActive = computed(() => voiceStore.isActive)
const audioLevel = computed(() => voiceStore.audioLevel)
const isConnecting = computed(() => voiceStore.isConnecting)
const error = computed(() => voiceStore.error)
const agentAudioLevel = computed(() => voiceStore.agentAudioLevel)

const isUserSpeaking = computed(() => isActive.value && audioLevel.value > 5)
const isAiSpeaking = computed(() => isActive.value && agentAudioLevel.value > 5)

const indicatorColor = computed(() => {
  if (error.value) return 'bg-destructive'
  if (isActive.value) return 'bg-primary'
  if (isConnecting.value) return 'bg-yellow-500'
  return 'bg-muted'
})

const haloStyle = computed(() => {
  if (isUserSpeaking.value) {
    const scale = 1 + Math.min(audioLevel.value / 100, 0.5)
    return {
      transform: `scale(${scale})`,
      borderColor: 'rgba(59, 130, 246, 0.5)', // Blue for user
      opacity: '1',
    }
  }
  if (isAiSpeaking.value) {
    return {
      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
      borderColor: 'rgba(168, 85, 247, 0.5)', // Purple for AI
      opacity: '1',
    }
  }
  return {
    transform: 'scale(1)',
    opacity: '0',
  }
})
</script>

<template>
  <div
    class="relative flex items-center justify-center w-14 h-14 rounded-full shadow-lg cursor-pointer"
    :class="[indicatorColor]"
  >
    <!-- Halo effect -->
    <div
      class="absolute w-full h-full rounded-full border-4 transition-all duration-200 ease-out"
      :style="haloStyle"
    />

    <Icon v-if="isConnecting" name="svg-spinners:180-ring-with-bg" class="w-7 h-7 text-white" />
    <Icon v-else-if="isActive" name="heroicons:microphone-solid" class="w-7 h-7 text-white" />
    <Icon v-else name="heroicons:microphone" class="w-7 h-7 text-muted-foreground" />
  </div>
</template>

<style scoped>
@keyframes pulse {
  50% {
    opacity: 0.5;
  }
}
</style>
